import { useEffect, useRef, useState } from 'react';
import type { TopicMessage, WebSocketStatus } from './WebSocketClient';
import { useWebSocketClient } from './WebSocketProvider';

type UseWebSocketTopicOptions = {
  /** Runs whenever the shared socket (re)connects. Use it to reconcile any
   * state that may have drifted while the connection was down. */
  onReconnect?: () => void;
};

/**
 * Subscribe to one topic on the shared application socket.
 *
 * Returns the connection status, which matters for features where the socket
 * *is* the data rather than a notification channel -- a closed socket means
 * the cached view may be stale, not merely un-notified.
 */
export default function useWebSocketTopic(
  topic: string,
  onMessage: (message: TopicMessage) => void,
  { onReconnect }: UseWebSocketTopicOptions = {},
) {
  const client = useWebSocketClient();
  const [status, setStatus] = useState<WebSocketStatus>('closed');

  const callbacks = useRef({ onMessage, onReconnect });
  callbacks.current = { onMessage, onReconnect };

  useEffect(() => {
    if (!client) return;
    return client.on(topic, (message) => {
      callbacks.current.onMessage(message);
    });
  }, [client, topic]);

  useEffect(() => {
    if (!client) return;

    let wasOpen = false;
    return client.onStatusChange((next) => {
      setStatus(next);
      // Fires on the first connect too, which is what we want: a fresh
      // subscriber has no state yet and needs the same reconciliation.
      if (next === 'open' && !wasOpen) {
        callbacks.current.onReconnect?.();
      }
      wasOpen = next === 'open';
    });
  }, [client]);

  return status;
}
